// shell.js — the persistent product frame for showcases (ported from website2-shell.jsx):
// browser chrome, the real app sidebar (nami-nav classes) and top bar (nami-topbar classes),
// plus shared marketing bits: buttons, stickers, toasts, reveal hook, ghost cursor.
// NOTE: the design's useN2Parallax (mouse parallax on the hero stage) is intentionally skipped.
import React from 'react'
import { Link } from 'react-router-dom'
import { N2Ic, N2Paw, NIc } from './icons'

/* ── marketing buttons — `to` renders a router Link, `href` a plain anchor ── */
export function N2Btn({ href = '#pricing', to, target, rel, ghost = false, small = false, paw = false, onClick, children, className = '' }) {
	const cls = 'n2-btn ' + (ghost ? 'n2-btn-ghost' : 'n2-btn-primary') + (small ? ' sm' : '') + (className ? ' ' + className : '')
	const inner = (
		<>
			{paw ? <span className="n2-btn-paw"><N2Paw size={15} /></span> : null}
			{children}
		</>
	)
	if (to) return <Link className={cls} to={to} onClick={onClick}>{inner}</Link>
	return <a className={cls} href={href} target={target} rel={rel} onClick={onClick}>{inner}</a>
}

export function N2Sticker({ icon = 'spark', blush = false, style, children }) {
	return (
		<span className={'n2-sticker' + (blush ? ' blush' : '')} style={style}>
			<N2Ic name={icon} size={13} sw={2.2} />{children}
		</span>
	)
}

/* product-toast (marketing float) — mirrors the app's notification anatomy */
export function N2Toast({ ic = 'mint', icon = 'bell', title, sub }) {
	return (
		<div className="n2-toast">
			<span className={'n2-toast-ic ' + ic}><N2Ic name={icon} size={16} sw={2} /></span>
			<div className="n2-toast-tx"><strong>{title}</strong><span>{sub}</span></div>
		</div>
	)
}

/* scroll reveal — IO fallback for browsers without scroll-driven animations */
export function useN2Reveal() {
	React.useEffect(() => {
		if (CSS.supports('animation-timeline: view()')) {
			document.documentElement.classList.add('sda')
			return () => document.documentElement.classList.remove('sda')
		}
		const io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target) } }), { threshold: 0.16 })
		const seen = new WeakSet()
		const scan = () => document.querySelectorAll('.n2-reveal').forEach((el) => { if (!seen.has(el)) { seen.add(el); io.observe(el) } })
		scan()
		const mo = new MutationObserver(scan)
		mo.observe(document.body, { childList: true, subtree: true })
		return () => { io.disconnect(); mo.disconnect() }
	}, [])
}

/* phase machine for in-screen story beats. Default: timed — plays once `on` flips true, resets
   when it flips back. Inside an N2Scrub provider the clock is external: the store's q (0→1) is
   mapped onto the same timeline (a short idle, then from store.lead ms to the end), so the beats
   advance — and rewind — with whatever drives q (the mobile journey feeds it scroll). */
export const N2Scrub = React.createContext(null) // { get(), subscribe(fn), lead }
export function useN2Phases(on, steps) { // steps: [[ms, 'phase'], …]
	const scrub = React.useContext(N2Scrub)
	const reduce = React.useRef(window.matchMedia('(prefers-reduced-motion: reduce)').matches).current
	const [phase, setPhase] = React.useState(reduce ? steps[steps.length - 1][1] : 'idle')
	React.useEffect(() => {
		if (reduce) return
		if (scrub) {
			const total = steps[steps.length - 1][0] + 700, lead = Math.min(scrub.lead || 0, total)
			const calc = (q) => {
				const t = q <= 0.08 ? 0 : lead + (q - 0.08) / 0.92 * (total - lead)
				let ph = 'idle'
				for (const [ms, p] of steps) if (t >= ms) ph = p
				setPhase(ph)
			}
			calc(scrub.get())
			return scrub.subscribe(calc)
		}
		if (!on) { setPhase('idle'); return }
		const ids = steps.map(([ms, p]) => window.setTimeout(() => setPhase(p), ms))
		return () => ids.forEach(clearTimeout)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [on, scrub])
	return phase
}

/* ghost cursor that glides to a target and presses it */
export function N2Cursor({ show, x, y, press = false }) {
	return (
		<span className={'n2-cursor' + (show ? ' show' : '') + (press ? ' press' : '')} style={{ '--cx': x, '--cy': y }} aria-hidden="true">
			<svg width="22" height="22" viewBox="0 0 24 24">
				<path d="M5.5 3.2l13.2 7.6-5.8 1.5 3.4 6.6-2.7 1.4-3.4-6.6-4.7 4V3.2z" fill="#2E2A40" stroke="#FFFFFF" strokeWidth="1.6" strokeLinejoin="round"></path>
			</svg>
		</span>
	)
}

/* ── the real app chrome ─────────────────────────────────────────── */
export const N2_NAV_ITEMS = [
	{ k: 'dashboard', ic: 'dashboard', l: 'Dashboard' },
	{ k: 'calendar', ic: 'calendar', l: 'Calendar' },
	{ k: 'bookings', ic: 'bookings', l: 'Bookings' },
	{ k: 'clients', ic: 'pets', l: 'Clients/Pets' },
	{ k: 'employees', ic: 'employees', l: 'Employees' },
	{ k: 'services', ic: 'services', l: 'Services' },
	{ k: 'messages', ic: 'messages', l: 'Messages', badge: 2 },
	{ k: 'agreements', ic: 'agreements', l: 'Agreements' },
	{ k: 'landing', ic: 'landing', l: 'My Landing Page' }
]

/* collapsed icon rail — matches the real app's .nami-nav.collapsed state */
export function N2AppNav({ active }) {
	const idx = Math.max(0, N2_NAV_ITEMS.findIndex((n) => n.k === active))
	return (
		<aside className="nami-nav collapsed s-nav" aria-hidden="true">
			<div className="nav-head">
				<span className="nav-collapse" style={{ transform: 'scaleX(-1)' }}><NIc name="collapse" size={14} sw={1.8} /></span>
			</div>
			<div className="nav-items" style={{ position: 'relative' }}>
				<span className="s-nav-pill" style={{ '--i': idx }} aria-hidden="true"></span>
				{N2_NAV_ITEMS.map((n) => (
					<span key={n.k} className={'nav-item' + (n.k === active ? ' on' : '')} title={n.l}>
						<span className="nav-item-ic"><NIc name={n.ic} size={16} sw={1.7} /></span>
						{n.badge ? <em className="s-nav-badge">{n.badge}</em> : null}
					</span>
				))}
			</div>
			<div className="nav-divider" style={{ marginTop: 'auto' }}></div>
			<div className="nav-items">
				<span className="nav-item" title="Settings"><span className="nav-item-ic"><NIc name="settings" /></span></span>
				<span className="nav-item off" title="Support"><span className="nav-item-ic"><NIc name="help" /></span></span>
			</div>
		</aside>
	)
}

export function N2TopBar({ bare = false }) {
	return (
		<div className="nami-topbar s-topbar">
			{bare ? <div style={{ flex: 1 }}></div> : <div className="topbar-search"><NIc name="search" size={15} sw={1.8} /><span className="s-search-ph">Search clients, pets, appointments…</span></div>}
			<div className="topbar-actions">
				<span className="topbar-icbtn"><NIc name="help" size={17} sw={1.6} /></span>
				<span className="topbar-icbtn"><NIc name="bell" size={16} sw={1.6} /><i className="topbar-dot"></i></span>
				<span className="topbar-sep"></span>
				<span className="topbar-user">
					<span className="topbar-avatar">PG</span>
					<span className="topbar-user-id"><strong>Pawfect Grooming Co.</strong><span>Owner account</span></span>
				</span>
			</div>
		</div>
	)
}

/* browser window around the app — url morphs per screen */
export function N2Window({ url, children, mini = false }) {
	return (
		<div className={'n2-appwin' + (mini ? ' mini' : '')}>
			<div className="n2-winbar">
				<span className="n2-windots" aria-hidden="true"><i></i><i></i><i></i></span>
				<span className="n2-winurl" key={url}><N2Ic name="shield" size={11} sw={2.2} />{url}</span>
				<span className="n2-winlive"><i></i>Live</span>
			</div>
			{children}
		</div>
	)
}

/* fixed-size app screen scaled to its container. The design's pure-CSS route
   (tan(atan2()) length division with cqw units) is kept in the stylesheet for
   browsers that compute it correctly, but the scale is always driven from JS
   too: iOS Safari passes the @supports probe for tan(atan2()) yet resolves the
   cqw division to 0 — the screens rendered at scale(0), i.e. blank phones. The
   inline transform (set before first paint) overrides the CSS either way. */
export function N2Fit({ w = 1240, h = 780, children, className = '' }) {
	const ref = React.useRef(null)
	React.useLayoutEffect(() => {
		const el = ref.current, inner = el.firstChild
		const fit = () => { inner.style.transform = 'scale(' + (el.clientWidth / w) + ')' }
		fit()
		const ro = new ResizeObserver(fit)
		ro.observe(el)
		return () => ro.disconnect()
	}, [w])
	return (
		<div className={'n2-fitclip ' + className} ref={ref} style={{ '--dw': w, '--dh': h, aspectRatio: w + ' / ' + h }}>
			<div className="n2-fit nm v-lav">{children}</div>
		</div>
	)
}
